import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, fmtDate, priorityColors } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  { k: "scadute", l: "Scadute", cls: "border-rose-200 bg-rose-50", txt: "text-rose-900" },
  { k: "oggi", l: "Da fare oggi", cls: "border-amber-200 bg-amber-50", txt: "text-amber-900" },
  { k: "urgenti", l: "Urgenti", cls: "border-orange-200 bg-orange-50", txt: "text-orange-900" },
  { k: "prossime", l: "Prossime", cls: "border-slate-200 bg-white", txt: "text-slate-900" },
];

export const TodoBoard = () => {
  const [board, setBoard] = useState(null);
  const [quick, setQuick] = useState("");

  const load = async () => { const { data } = await http.get("/tasks-board"); setBoard(data); };
  useEffect(() => { load(); }, []);

  const addQuick = async (e) => {
    e.preventDefault();
    if (!quick.trim()) return;
    await http.post("/tasks", { title: quick.trim(), priority: "media", status: "da_fare" });
    setQuick(""); toast.success("Todo creato"); load();
  };
  const complete = async (t) => {
    await http.put(`/tasks/${t.id}`, { ...t, status: "completato" });
    load();
  };

  if (!board) return null;
  const c = board.counts || {};

  return (
    <section className="space-y-3" data-testid="dashboard-todo">
      <div className="flex items-baseline justify-between gap-3 pt-1">
        <h2 className="font-display text-lg font-bold text-slate-900">Todo · cose da fare</h2>
        <Link to="/todo" className="text-[11px] font-semibold text-[var(--teal-deep)] underline underline-offset-4">Tutti i todo →</Link>
      </div>

      <form onSubmit={addQuick} className="flex gap-2">
        <Input value={quick} onChange={(e) => setQuick(e.target.value)} data-testid="todo-quick-input"
          placeholder="Aggiungi rapidamente (anche senza appartamento) — es. Chiamare commercialista" className="rounded-xl" />
        <Button type="submit" data-testid="todo-quick-add" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Plus className="h-4 w-4" /></Button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {GROUPS.map((g) => (
          <Card key={g.k} className={`rounded-2xl p-4 border ${g.cls}`} data-testid={`todo-group-${g.k}`}>
            <div className="flex items-center justify-between">
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${g.txt}`}>{g.l}</div>
              <span className={`font-display text-lg font-extrabold ${g.txt} tabular-nums`} data-testid={`todo-count-${g.k}`}>{c[g.k] || 0}</span>
            </div>
            <div className="mt-2 space-y-1.5">
              {(board[g.k] || []).length === 0 && <div className="text-xs text-slate-400">Nulla qui</div>}
              {(board[g.k] || []).slice(0, 4).map((t) => (
                <div key={t.id} className="flex items-start gap-2 text-sm" data-testid={`todo-item-${t.id}`}>
                  <button onClick={() => complete(t)} data-testid={`todo-complete-${t.id}`} title="Completa"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-slate-300 bg-white flex items-center justify-center hover:border-emerald-500 hover:text-emerald-600"><Check className="h-3 w-3 opacity-0 hover:opacity-100" /></button>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 leading-snug break-words">{t.title}</div>
                    <div className="text-[11px] text-slate-500 flex flex-wrap gap-2">
                      {t.apartment_name && <span>{t.apartment_name}</span>}
                      {t.due_date && <span>{fmtDate(t.due_date)}</span>}
                      {t.priority !== "media" && <Badge variant="outline" className={`${priorityColors[t.priority]} text-[10px] py-0`}>{t.priority}</Badge>}
                      {t.source_label && <span className="inline-flex items-center gap-1 text-[var(--teal-deep)]"><ExternalLink className="h-3 w-3" />{t.source_label}</span>}
                    </div>
                  </div>
                </div>
              ))}
              {(board[g.k] || []).length > 4 && <Link to="/todo" className="text-[11px] text-[var(--teal-deep)] underline">+{(board[g.k] || []).length - 4} altri</Link>}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default TodoBoard;
